import React from "react";
import DashboardWidgetContainer from "../../../containers/DashboardWidget/DashboardWidgetContainer";
import { widgetKeys } from "../../../constants";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import CircularLoaderChart from "../../Charts/CircularLoaderChart";
import { useCompanyCompliance } from "../../../hooks/company_compliance";
import { useState } from "react";
import { Tooltip } from "../../Tooltip";
import Skeleton from "../../Skeleton/Skeleton";
import { useTrainingObjectTypes } from "../../../hooks/trainingObjectTypes";

const useStyles = makeStyles(() => ({
  containerMain: {
    display: "flex",
    flexWrap: "wrap"
  },
  item: {
    display: "flex",
    flex: "1",
    alignItems: "center",
    flexDirection: "column",
    justifyContent: "center"
  }
}));

const SkeletonWidget = () => {
  const classes = useStyles();
  return (
    <Box className={classes.containerMain} width="100%">
      <Box className={classes.item}>
        <Skeleton
          loading
          variant="rectangular"
          height={20}
          width="40%"
          mb={2}
          mt={2}
        ></Skeleton>
        <Skeleton
          loading
          variant="rectangular"
          height={14}
          mb={2}
          width="85%"
        ></Skeleton>
        <Skeleton
          loading
          variant="circular"
          height={240}
          width={240}
        ></Skeleton>
      </Box>
      <Box className={classes.item}>
        <Skeleton
          loading
          variant="rectangular"
          height={20}
          width="45%"
          mb={2}
          mt={2}
        ></Skeleton>
        <Skeleton
          loading
          variant="rectangular"
          height={14}
          width="55%"
          mb={1}
        ></Skeleton>
        <Skeleton
          loading
          variant="rectangular"
          height={14}
          width="50%"
          mb={2}
        ></Skeleton>
        <Skeleton
          loading
          variant="circular"
          height={130}
          width={130}
        ></Skeleton>
      </Box>
    </Box>
  );
};

const CompanyCompliance = ({ dashboard }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [filter, setFilter] = useState({ label: "All", value: "all" });
  const { compliance, isLoading } = useCompanyCompliance(filter);
  const { trainingObjectTypeOptions } = useTrainingObjectTypes("assignable");

  return (
    <DashboardWidgetContainer
      dashboard={dashboard}
      widgetKey={widgetKeys.companyCompliance}
      title={
        <Tooltip title={t("companyComplianceWidget.titleTooltip")}>
          <div>{t("companyComplianceWidget.title")}</div>
        </Tooltip>
      }
      dropdownProps={{
        options: trainingObjectTypeOptions,
        value: filter,
        onChange: value => {
          setFilter(value);
        }
      }}
    >
      <Box className={classes.containerMain}>
        {isLoading ? (
          <SkeletonWidget />
        ) : compliance ? (
          <>
            <Box className={classes.item}>
              <Typography
                variant="body1"
                color="textPrimary"
                sx={{ mb: 1, mt: 2 }}
              >
                <b>{t("companyComplianceWidget.myCompanyTitle")}</b>
              </Typography>
              <Typography variant="body1" color="textPrimary" sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "inline" }}
                >
                  {t("companyComplianceWidget.myCompanyText")}
                </Typography>
              </Typography>
              <CircularLoaderChart
                size={240}
                thickness={9}
                value={compliance.company}
                text={
                  <Typography variant="h3" color="textPrimary">
                    {compliance.company}%
                  </Typography>
                }
              />
            </Box>
            <Box className={classes.item}>
              <Typography
                variant="body1"
                color="textPrimary"
                sx={{ mb: 1, mt: 2 }}
              >
                <b>{t("companyComplianceWidget.industryTitle")}</b>
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "inline" }}
              >
                {t("companyComplianceWidget.industryTextLine1")}
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: "inline", mb: 2 }}
              >
                {t("companyComplianceWidget.industryTextLine2")}
              </Typography>
              <CircularLoaderChart
                size={130}
                thickness={7}
                value={
                  compliance?.industry
                    ? compliance.industry.replace(/%/, "")
                    : "0"
                }
                text={
                  <Typography variant="h6" color="textPrimary">
                    {" "}
                    {compliance.industry}
                  </Typography>
                }
              />
            </Box>
          </>
        ) : null}
      </Box>
    </DashboardWidgetContainer>
  );
};

export default withRouter(CompanyCompliance);

CompanyCompliance.propTypes = {
  dashboard: PropTypes.string
};
